import React, { useRef, useState } from 'react';
import { Files } from '../lib/api';
import { UploadCloud, Loader2, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from '../hooks/use-toast';

export default function ImageDropzone({ value, onChange, label = 'Drop image or click to upload', aspect = 'square' }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const aspectClass =
    aspect === 'banner' ? 'aspect-[4/1]'
      : aspect === 'video' ? 'aspect-video'
      : 'aspect-square';
  const fitClass = aspect === 'banner' ? 'object-contain bg-stone-50' : 'object-cover';

  const handle = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Not an image', description: `${file.name} is type ${file.type || 'unknown'}` });
      return;
    }
    setUploading(true);
    try {
      const res = await Files.upload(file);
      onChange(res.url);
      toast({ title: 'Image uploaded', description: `${file.name} (${Math.round(file.size / 1024)} KB)` });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || String(e);
      const status = e?.response?.status;
      toast({
        title: status === 413 ? 'File too large' : 'Upload failed',
        description: detail
      });
      console.error('ImageDropzone upload failed:', e);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.[0]) handle(e.dataTransfer.files[0]);
  };

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={cn(
        'relative rounded-md border-2 border-dashed cursor-pointer transition-colors overflow-hidden',
        aspectClass,
        dragOver ? 'border-stone-700 bg-stone-50' : value ? 'border-stone-200' : 'border-stone-300 hover:border-stone-400'
      )}
    >
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handle(e.target.files?.[0])} />
      {uploading && (
        <div className="absolute inset-0 grid place-items-center bg-white/70 z-10">
          <Loader2 className="w-6 h-6 animate-spin text-stone-600" />
        </div>
      )}
      {value ? (
        <>
          <img src={value} alt="" className={cn('w-full h-full', fitClass)} />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
            className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full w-6 h-6 grid place-items-center"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </>
      ) : (
        <div className="absolute inset-0 grid place-items-center text-stone-400">
          <div className="text-center px-4">
            <UploadCloud className="w-7 h-7 mx-auto mb-1" />
            <div className="text-[12px]">{label}</div>
          </div>
        </div>
      )}
    </div>
  );
}
